import { ChangeDetectionStrategy, Component, ElementRef, output, viewChild } from '@angular/core';

type DemoMethod = {
  id: 'upload' | 'type' | 'template' | 'ai';
  title: string;
  desc: string;
  tag?: string;
};

/** Warns when overview copy points at an external brief, and shows how to use Document instead. */
@Component({
  selector: 'bb-external-brief-link-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './external-brief-link-modal.html',
  styleUrl: './external-brief-link-modal.scss',
  host: {
    class: 'contents',
  },
})
export class ExternalBriefLinkModal {
  private readonly dialogRef = viewChild<ElementRef<HTMLDialogElement>>('dialog');

  readonly useDocument = output<void>();

  readonly methods: DemoMethod[] = [
    { id: 'upload', title: 'Document', desc: 'Bring an existing brief — PDF, DOCX, PPTX or Google Doc.' },
    { id: 'type', title: 'Type Requirements', desc: 'Write the concepts and shot list yourself.' },
    { id: 'template', title: 'Select Template', desc: 'Start from one of your saved templates.' },
    { id: 'ai', title: 'Help Me', desc: 'Let SHOUT AI draft it from your brief.', tag: 'AI' },
  ];

  open(): void {
    const dialog = this.dialogRef()?.nativeElement;
    if (!dialog || dialog.open) {
      return;
    }
    dialog.showModal();
  }

  close(): void {
    const dialog = this.dialogRef()?.nativeElement;
    if (dialog?.open) {
      dialog.close();
    }
  }

  protected cardClass(id: DemoMethod['id']): string {
    const base = 'relative flex flex-col items-start gap-1 rounded-lg border border-border bg-card p-2.5 text-left';
    return id === 'upload' ? `${base} demo-card-document` : base;
  }

  protected iconClass(id: DemoMethod['id']): string {
    const base = 'demo-icon mb-1 grid size-8 place-items-center rounded-md bg-muted text-muted-foreground';
    return id === 'upload' ? `${base} demo-icon-document` : base;
  }

  protected chooseDocument(): void {
    this.close();
    this.useDocument.emit();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === this.dialogRef()?.nativeElement) {
      this.close();
    }
  }
}
